import { useCallback, useEffect } from 'react';
import { Field, Dropdown, useFormState, useFormApi } from '@carboledger/ui';
import { EMPTY_LIST } from '@/lib/utils';

/**
 * Props:
 *   name         form field name
 *   label        the field's own label
 *   placeholder  shown while nothing is chosen
 *   emptyMessage shown when there are no options
 *   options      Option[]
 *   clears       downstream field names to reset when this one changes
 *   searchable · searchPlaceholder · noResultsMessage  for a long list
 *   required     defaults to true
 *   rules        extra validation rules on top of required
 *   disabled     locks the control
 *   ready        false while options are still loading, so a prefilled value survives
 */
export const DependentDropdownField = ({
  name,
  label,
  placeholder,
  emptyMessage,
  options = EMPTY_LIST,
  clears = EMPTY_LIST,
  searchable = false,
  searchPlaceholder,
  noResultsMessage,
  required = true,
  rules,
  disabled = false,
  ready = true,
}) => {
  const { values } = useFormState();
  const { setValue } = useFormApi();
  const value = values[name];

  // An upstream change can strand the value; its descendants were derived from it, so they go too.
  useEffect(() => {
    if (!ready) return;
    if (value && !options.some((option) => String(option.value) === String(value))) {
      setValue(name, '');
      clears.forEach((field) => setValue(field, ''));
    }
  }, [ready, options, value, name, clears, setValue]);

  const handleChange = useCallback(
    (event) => {
      setValue(name, event.target.value);
      clears.forEach((field) => setValue(field, ''));
    },
    [name, clears, setValue],
  );

  return (
    <Field name={name} required={required} rules={rules}>
      {({ id, onBlur, status, message, required: isRequired }) => (
        <Dropdown
          id={id}
          name={name}
          label={label}
          placeholder={placeholder}
          searchable={searchable}
          searchPlaceholder={searchPlaceholder}
          noResultsMessage={noResultsMessage}
          value={value ?? ''}
          onChange={handleChange}
          onBlur={onBlur}
          status={status}
          message={message}
          required={isRequired}
          disabled={disabled}
          options={options}
          emptyMessage={emptyMessage}
        />
      )}
    </Field>
  );
};
